import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockSimple, SlidersHorizontal, SparkleIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useCaseFrameLeaveGuard } from "@/zapi-frontend/stores/caseFrameLeaveGuard";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useProjectSetupStore } from "@/zapi-frontend/stores/projectSetup";
import { selectIsChapterGenerating, useChatStore } from "@/zapi-frontend/stores/chatStore";
import { selectIsLeoEditingChapter, useLeoChatStore } from "@/zapi-frontend/stores/leoChatStore";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";
import { TChapter } from "@/zapi-frontend/shared/types/projects";
import { ClickAwayListener } from "@libs/zapi-ui/custom";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { SidebarButton, SidebarGroupLabel, SidebarSubMenu } from "./SidebarButton";
import { useModelLedger } from "../ModelLedgerIsland/useModelLedger";
import { useRqaSimTwin } from "../RqaSimIsland/useRqaSim";
import {
  SidebarHomeIcon,
  SidebarMemoIcon,
  SidebarFrameIcon,
  SidebarVaultIcon,
  SidebarLedgerIcon,
  SidebarExportIcon,
  SidebarExecSummaryIcon
} from "@libs/zapi-ui/assets";
import { SyncedSpinner } from "@/zapi-frontend/shared/ui/SyncedSpinner";
import {
  DEEP_DIVE_SECTIONS,
  EXEC_SUMMARY_SLUG,
  FULL_SUMMARY_SLUG,
  getExecSummaryLabelKey,
  isAppendixChapter
} from "@/zapi-frontend/shared/lib/projectChapters";

export { DEEP_DIVE_SECTIONS, EXEC_SUMMARY_SLUG, FULL_SUMMARY_SLUG, isAppendixChapter };

export type TCreationView = "projectSetup" | "caseFrame";

type TProjectSidebarProps = {
  creation?: {
    view: TCreationView;
    onViewChange: (view: TCreationView) => void;
    caseFrameLocked: boolean;
  };
};

function LockBadge() {
  return <LockSimple className="size-3 shrink-0 text-innoveraZinc-400" weight="regular" />;
}

// Only a chapter the chat is changing gets a spinner: generated by the chat, or edited by Leo.
// During project setup every chapter is generating at once and the chat progress list already
// reports each of them (INV-2334).
function ChapterIndicator({ chapter, projectId }: { chapter: TChapter; projectId: string }) {
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId]);
  const isGenerating = useChatStore(selectIsChapterGenerating(projectId, String(chapter.id)));
  const isLeoEditing = useLeoChatStore(selectIsLeoEditingChapter(projectId, chapter.projectTemplateChapter.slug));

  if (isSetupActive || !(isGenerating || isLeoEditing)) return null;
  return <SyncedSpinner />;
}

export function ProjectSidebar({ creation }: TProjectSidebarProps) {
  const [, setSearchParams] = useSearchParams();
  const { id: projectId } = useParams() as { id: string };
  const { isSidebarDocked } = useWorkspaceLayout();
  const { t } = useTranslation("projectHome");

  const { selectedProject } = useProjectStore();
  const {
    projectChapters,
    activeChapter,
    setActiveChapter,
    isOpenChapterSidebar,
    toggleChapterSidebar,
    viewMode,
    setViewMode
  } = useChapterStore();
  const { isLabs } = useMeStore();
  const { closeHandler } = useProjectSidebarStore();
  const { isDirty: isCaseFrameDirty, setPendingLeave: setCaseFramePendingLeave } = useCaseFrameLeaveGuard();

  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(creation ? ["frame"] : []));

  const chapters = useMemo(() => {
    const execChapter =
      projectChapters.find((c) => c.projectTemplateChapter.slug === EXEC_SUMMARY_SLUG) ??
      projectChapters.find(
        (c) => c.projectTemplateChapter.isExecutiveSummary && c.projectTemplateChapter.slug !== FULL_SUMMARY_SLUG
      );
    const memoChapter = projectChapters.find((c) => c.projectTemplateChapter.slug === FULL_SUMMARY_SLUG);
    const specialIds = new Set([execChapter?.id, memoChapter?.id]);

    const appendices = projectChapters.filter((c) => !specialIds.has(c.id) && !c.isHidden && isAppendixChapter(c));
    const regular = projectChapters.filter((c) => !specialIds.has(c.id) && !c.isHidden && !isAppendixChapter(c));

    const templateHasSections = regular.some((c) => c.projectTemplateChapter.section);
    const sections = DEEP_DIVE_SECTIONS.map((s) => ({
      ...s,
      chapters: regular.filter((c) => c.projectTemplateChapter.section === s.key)
    }));
    // A regular chapter without a section means the template marks it hidden (or exec) —
    // e.g. an old project where the chapter is still visible. Group it under Hidden Chapters.
    // If the template has no sections at all (not migrated yet), keep the flat list instead.
    const unsectioned = templateHasSections ? [] : regular.filter((c) => !c.projectTemplateChapter.section);
    const hiddenChapters = [
      ...projectChapters.filter((c) => !specialIds.has(c.id) && c.isHidden),
      ...(templateHasSections ? regular.filter((c) => !c.projectTemplateChapter.section) : [])
    ];

    return { execChapter, memoChapter, appendices, hiddenChapters, sections, unsectioned };
  }, [projectChapters]);

  const activeChapterId = activeChapter?.id;

  useEffect(() => {
    if (!activeChapterId) return;
    const owningSection = chapters.sections.find((s) => s.chapters.some((c) => c.id === activeChapterId));
    if (owningSection) {
      setExpandedSections((prev) => (prev.has(owningSection.key) ? prev : new Set(prev).add(owningSection.key)));
    }
    if (chapters.appendices.some((c) => c.id === activeChapterId)) {
      setExpandedSections((prev) => (prev.has("appendices") ? prev : new Set(prev).add("appendices")));
    }
    if (chapters.hiddenChapters.some((c) => c.id === activeChapterId)) {
      setExpandedSections((prev) => (prev.has("hidden") ? prev : new Set(prev).add("hidden")));
    }
  }, [activeChapterId, chapters]);

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const guardCaseFrameLeave = (leave: () => void) => {
    if (viewMode === "projectFrame" && isCaseFrameDirty) {
      setCaseFramePendingLeave(leave);
      return;
    }
    leave();
  };

  const openChapter = (chapterId: number) => {
    if (!isSidebarDocked) toggleChapterSidebar(false);
    if (viewMode === "chapter" && activeChapter?.id === chapterId) return;
    guardCaseFrameLeave(() => {
      setActiveChapter(chapterId.toString());
      setViewMode("chapter");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "document");
        params.set("chapterId", chapterId.toString());
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });
    });
  };

  const openHome = () => {
    if (!isSidebarDocked) toggleChapterSidebar(false);
    if (viewMode === "dashboard" || viewMode === "projectOverview") return;
    guardCaseFrameLeave(() => {
      closeHandler();
      setViewMode("projectOverview");
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "overview");
        params.delete("chapterId");
        params.delete("chapterSubsection");
        params.delete("view");
        return params;
      });
    });
  };

  const openFrameView = (
    view: "projectFrame" | "projectConfig" | "vault" | "exports" | "valueChain" | "modelLedger" | "rqaSim" | "risks"
  ) => {
    if (!isSidebarDocked) toggleChapterSidebar(false);
    const navigateToView = () => {
      closeHandler();
      setViewMode(view);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", view);
        params.delete("chapterId");
        params.delete("chapterSubsection");
        return params;
      });
    };
    if (view === "projectFrame") navigateToView();
    else guardCaseFrameLeave(navigateToView);
  };

  const isHomeActive = viewMode === "dashboard" || viewMode === "projectOverview";

  // Per Figma the value chain is a page of Market Analysis, not a Context entry.
  // Template-less projects carry the value chain as a chain block inside a generated
  // Market Analysis chapter, so they never show the standalone page.
  const VALUE_CHAIN_SECTION = "market-analysis";
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const hasValueChain = useFeatureFlag("value-chain", selectedProject?.organizationId) && !isTemplateLess;
  // The ledger exists only for projects whose generation produced one, so the entry follows the data
  // and is re-read once a generation on this project finishes.
  const { data: modelLedger } = useModelLedger(creation ? "" : projectId);
  const hasModelLedger = !!modelLedger;
  // The twin exists only where the run produced one that passed its gates, so the row follows the
  // data exactly as the ledger's does; the flag keeps it off while it is research.
  const rqaSimEnabled = useFeatureFlag("rqa-sim", selectedProject?.organizationId);
  // Behind the flag, or every project page of every organisation asks for a twin the route answers
  // 404 to, three times over, before anything decides whether the row can appear at all.
  const { data: rqaSimTwin } = useRqaSimTwin(creation || !rqaSimEnabled ? "" : projectId);
  const hasRqaSim = rqaSimEnabled && !!rqaSimTwin;
  const isSetupActive = useProjectSetupStore((s) => !!s.activeSetupTasks[projectId]);
  const [searchParamsForDebug] = useSearchParams();
  const isDebugSettingUp = (isLabs || import.meta.env.DEV) && searchParamsForDebug.get("debugGenState") === "settingUp";
  // Vault and Exports are blocked while the project sets up, the same rule the header's Export as
  // button uses, so every item that looks clickable during setup also opens (INV-2514).
  const isSetupBlocking =
    useProjectSetupStore((s) => !!s.pollingProjects[projectId] || !!s.activeSetupTasks[projectId]) || isDebugSettingUp;
  const showSidebarSkeleton =
    !creation && ((isSetupActive && isTemplateLess && projectChapters.length === 0) || isDebugSettingUp);

  const renderChapterItem = (chapter: TChapter) => (
    <SidebarButton
      key={chapter.id}
      testId={`chapter-${chapter.id}-item`}
      label={chapter.projectTemplateChapter.nav || chapter.projectTemplateChapter.name}
      active={viewMode === "chapter" && activeChapter?.id === chapter.id}
      onClick={() => openChapter(chapter.id)}
      right={<ChapterIndicator chapter={chapter} projectId={projectId} />}
    />
  );

  const renderSectionGroup = (key: string, label: string, icon: React.ReactNode, sectionChapters: TChapter[]) => {
    if (!sectionChapters.length) return null;
    const isExpanded = expandedSections.has(key);
    // When a section is collapsed its submenu is hidden, so the section row itself carries the
    // active chapter's highlight.
    const holdsActiveChapter =
      (viewMode === "chapter" && sectionChapters.some((chapter) => chapter.id === activeChapter?.id)) ||
      (key === VALUE_CHAIN_SECTION && viewMode === "valueChain");
    return (
      <div key={key} className="w-full">
        <SidebarButton
          testId={`sidebar-section-${key}-button`}
          icon={icon}
          label={label}
          active={!isExpanded && holdsActiveChapter}
          caret={isExpanded ? "up" : "down"}
          onClick={() => toggleSection(key)}
        />
        <SidebarSubMenu expanded={isExpanded}>
          {sectionChapters.map((chapter) => renderChapterItem(chapter))}
          {key === VALUE_CHAIN_SECTION && !creation && hasValueChain && (
            <SidebarButton
              testId="sidebar-value-chain-button"
              label={t("sidebar.valueChain")}
              active={viewMode === "valueChain"}
              onClick={() => openFrameView("valueChain")}
            />
          )}
        </SidebarSubMenu>
      </div>
    );
  };

  const onClickAway = () => {
    if (!creation && !isSidebarDocked && isOpenChapterSidebar) toggleChapterSidebar(false);
  };

  return (
    <ClickAwayListener insideSelectors={["[data-chapter-sidebar-toggle]"]} onClickAway={onClickAway}>
      <div
        data-testid={creation ? "create-project-sidebar" : "sidebar-chapters-root"}
        className={cn(
          "flex flex-col bg-white pt-2 w-60",
          creation || isSidebarDocked
            ? "static h-auto shrink-0"
            : cn(
                "absolute top-16 bottom-2 z-30 transition-[left] duration-300",
                isOpenChapterSidebar ? "left-2 border-r border-innoveraZinc-100" : "-left-64"
              )
        )}
      >
        {!creation && !isSidebarDocked && isOpenChapterSidebar && (
          <span className="pointer-events-none absolute inset-y-0 left-full w-[23px] bg-linear-to-r from-black/5 to-transparent" />
        )}
        <div className="flex-1 flex flex-col w-full gap-4 px-2 overflow-y-auto overflow-x-hidden custom-thin-scrollbar pb-2">
          <div className="flex flex-col gap-px w-full">
            <SidebarButton
              icon={<SidebarHomeIcon />}
              label={t("sidebar.home")}
              active={!creation && isHomeActive}
              disabled={!!creation}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation ? undefined : openHome}
              testId="sidebar-home"
            />
            {/* While the analysis decides the structure these rows are blocked, so they use the plain
            disabled look to read apart from Home, Frame, Vault and Exports, which stay open. No
            spinners: every one of them is generating at once (INV-2514). */}
            {showSidebarSkeleton && (
              <>
                <SidebarButton
                  icon={<SidebarExecSummaryIcon />}
                  label={t("sidebar.executiveSummary")}
                  disabled
                />
                <SidebarButton
                  icon={<SidebarMemoIcon />}
                  label={t("sidebar.innoveraMemo")}
                  disabled
                />
                <SidebarButton
                  icon={<SlidersHorizontal className="size-4 shrink-0" weight="regular" />}
                  label={t("sidebar.rqaSim")}
                  disabled
                />
              </>
            )}
            {!showSidebarSkeleton && (creation || chapters.execChapter) && (
              <SidebarButton
                testId="sidebar-executive-summary-button"
                icon={isTemplateLess ? <SidebarMemoIcon /> : <SidebarExecSummaryIcon />}
                label={t(getExecSummaryLabelKey(isTemplateLess))}
                active={!creation && viewMode === "chapter" && activeChapter?.id === chapters.execChapter?.id}
                disabled={!!creation}
                onClick={creation ? undefined : () => openChapter(chapters.execChapter!.id)}
                right={
                  creation ? <LockBadge /> : <ChapterIndicator chapter={chapters.execChapter!} projectId={projectId} />
                }
              />
            )}
            {!showSidebarSkeleton && (creation || chapters.memoChapter) && (
              <SidebarButton
                testId="sidebar-memo-button"
                icon={<SidebarMemoIcon />}
                label={t("sidebar.innoveraMemo")}
                active={!creation && viewMode === "chapter" && activeChapter?.id === chapters.memoChapter?.id}
                disabled={!!creation}
                onClick={creation ? undefined : () => openChapter(chapters.memoChapter!.id)}
                right={
                  creation ? <LockBadge /> : <ChapterIndicator chapter={chapters.memoChapter!} projectId={projectId} />
                }
              />
            )}
            {!showSidebarSkeleton && !creation && rqaSimEnabled && (
              <SidebarButton
                testId="sidebar-risks-button"
                icon={<SlidersHorizontal className="size-4 shrink-0" weight="regular" />}
                label={t("sidebar.rqa")}
                active={viewMode === "risks"}
                onClick={() => openFrameView("risks")}
              />
            )}
          </div>

          {creation ? (
            <div className="flex flex-col gap-px w-full">
              <SidebarGroupLabel label={t("sidebar.deepDives")} />
              {DEEP_DIVE_SECTIONS.map(({ key, labelKey, Icon }) => (
                <SidebarButton key={key} icon={<Icon />} label={t(labelKey)} disabled right={<LockBadge />} />
              ))}
            </div>
          ) : showSidebarSkeleton ? (
            <div className="flex flex-col gap-px w-full">
              <SidebarGroupLabel label={t("sidebar.deepDives")} />
              {DEEP_DIVE_SECTIONS.map(({ key, labelKey, Icon }) => (
                <SidebarButton key={key} icon={<Icon />} label={t(labelKey)} disabled />
              ))}
            </div>
          ) : (
            (chapters.sections.some((s) => s.chapters.length > 0) || chapters.unsectioned.length > 0) && (
              <div className="flex flex-col gap-px w-full">
                <SidebarGroupLabel label={t("sidebar.deepDives")} />
                {chapters.sections.map((section) =>
                  renderSectionGroup(section.key, t(section.labelKey), <section.Icon />, section.chapters)
                )}
                {chapters.unsectioned.map((chapter) => renderChapterItem(chapter))}
                {chapters.appendices.length > 0 &&
                  renderSectionGroup(
                    "appendices",
                    t("sidebar.appendices"),
                    <SparkleIcon className="size-4 shrink-0" weight="regular" />,
                    chapters.appendices
                  )}
                {isLabs &&
                  chapters.hiddenChapters.length > 0 &&
                  renderSectionGroup(
                    "hidden",
                    t("sidebar.hiddenChapters"),
                    <SparkleIcon className="size-4 shrink-0" weight="regular" />,
                    chapters.hiddenChapters
                  )}
              </div>
            )
          )}

          <div className="flex flex-col gap-px w-full">
            <SidebarGroupLabel label={t("sidebar.context")} />
            <div className="w-full">
              <SidebarButton
                testId="sidebar-frame-button"
                icon={<SidebarFrameIcon />}
                label={t("sidebar.frame")}
                active={!expandedSections.has("frame") && (viewMode === "projectConfig" || viewMode === "projectFrame")}
                caret={expandedSections.has("frame") ? "up" : "down"}
                onClick={() => toggleSection("frame")}
              />
              <SidebarSubMenu expanded={expandedSections.has("frame")}>
                {creation ? (
                  <>
                    <SidebarButton
                      testId="sidebar-project-setup-button"
                      label={t("sidebar.projectSetup")}
                      active={creation.view === "projectSetup"}
                      onClick={() => creation.onViewChange("projectSetup")}
                    />
                    <SidebarButton
                      testId="sidebar-case-frame-button"
                      label={t("sidebar.caseFrame")}
                      active={creation.view === "caseFrame"}
                      disabled={creation.caseFrameLocked}
                      right={creation.caseFrameLocked ? <LockBadge /> : undefined}
                      onClick={creation.caseFrameLocked ? undefined : () => creation.onViewChange("caseFrame")}
                    />
                  </>
                ) : (
                  <>
                    <SidebarButton
                      testId="sidebar-project-setup-button"
                      label={t("sidebar.projectSetup")}
                      active={viewMode === "projectConfig"}
                      onClick={() => openFrameView("projectConfig")}
                    />
                    <SidebarButton
                      testId="sidebar-case-frame-button"
                      label={t("sidebar.caseFrame")}
                      active={viewMode === "projectFrame"}
                      onClick={() => openFrameView("projectFrame")}
                    />
                  </>
                )}
              </SidebarSubMenu>
            </div>
            <SidebarButton
              testId="sidebar-vault-button"
              icon={<SidebarVaultIcon />}
              label={t("sidebar.vault")}
              active={!creation && viewMode === "vault"}
              disabled={!!creation || isSetupBlocking}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation || isSetupBlocking ? undefined : () => openFrameView("vault")}
            />
            {!showSidebarSkeleton && !creation && hasModelLedger && (
              <SidebarButton
                testId="sidebar-model-ledger-button"
                icon={<SidebarLedgerIcon />}
                label={t("sidebar.modelLedger")}
                active={viewMode === "modelLedger"}
                onClick={() => openFrameView("modelLedger")}
              />
            )}
          </div>

          <div className="flex flex-col gap-px w-full">
            <SidebarGroupLabel label={t("sidebar.artifacts")} />
            <SidebarButton
              testId="sidebar-exports-button"
              icon={<SidebarExportIcon />}
              label={t("sidebar.exports")}
              active={!creation && viewMode === "exports"}
              disabled={!!creation || isSetupBlocking}
              right={creation ? <LockBadge /> : undefined}
              onClick={creation || isSetupBlocking ? undefined : () => openFrameView("exports")}
            />
            {!showSidebarSkeleton && !creation && hasRqaSim && (
              <SidebarButton
                testId="sidebar-rqa-sim-button"
                icon={<SlidersHorizontal className="size-4 shrink-0" weight="regular" />}
                label={t("sidebar.rqaSim")}
                active={viewMode === "rqaSim"}
                onClick={() => openFrameView("rqaSim")}
              />
            )}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}
